import React, { useState } from 'react'
import useSWR from 'swr'
import { fetcher, apiRequest } from '../utils/api'
import { DollarSign, CreditCard, Tag, TrendingUp, Crown, Receipt, ExternalLink, RefreshCw } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import { Button } from '../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import {
  OverviewTab,
  TransactionsTab,
  PromosTab,
  SubscriptionsTab,
  InvoicesTab,
  StripeTab,
  InsightsTab
} from '../components/payments'

export default function Payments() {
  const [activeTab, setActiveTab] = useState('overview')
  const [days, setDays] = useState(30)
  const [paymentPage, setPaymentPage] = useState(1)
  const [paymentStatus, setPaymentStatus] = useState('')
  
  const [subPage, setSubPage] = useState(1)
  const [subPlan, setSubPlan] = useState('')
  const [subActionLoading, setSubActionLoading] = useState(null)
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [extendDays, setExtendDays] = useState(14)
  
  const [invPage, setInvPage] = useState(1)
  const [invStatus, setInvStatus] = useState('')
  const [invActionLoading, setInvActionLoading] = useState(null)
  
  const [newPromo, setNewPromo] = useState({ code: '', plan: 'premium', duration_days: 365, max_uses: 1, description: '' })
  const [creating, setCreating] = useState(false)
  const [syncing, setSyncing] = useState(false)
  
  const { data: revenueData, isLoading: revenueLoading, mutate: refreshRevenue } = useSWR(`/api/admin/revenue?days=${days}`, fetcher, { revalidateOnFocus: false, dedupingInterval: 60000 })
  const { data: stripeData, isLoading: stripeLoading, mutate: refreshStripe } = useSWR('/api/admin/stripe/dashboard', fetcher, { revalidateOnFocus: false, dedupingInterval: 60000 })
  const { data: webhookEvents, mutate: refreshWebhooks } = useSWR('/api/admin/stripe/events?limit=20', fetcher)
  const { data: promoCodes, mutate: refreshPromos } = useSWR('/api/admin/promo-codes', fetcher, { revalidateOnFocus: false })
  const { data: paymentsData, isLoading: paymentsLoading, mutate: refreshPayments } = useSWR(`/api/admin/payments?page=${paymentPage}&limit=20${paymentStatus ? `&status=${paymentStatus}` : ''}`, fetcher, { revalidateOnFocus: false })
  const { data: subsData, isLoading: subsLoading, mutate: refreshSubs } = useSWR(`/api/admin/subscriptions?page=${subPage}&limit=15&plan=${subPlan}`, fetcher)
  const { data: subsSummary } = useSWR('/api/admin/subscriptions/summary', fetcher)
  const { data: invoicesData, isLoading: invoicesLoading, mutate: refreshInvoices } = useSWR(`/api/admin/invoices?page=${invPage}&limit=15&status=${invStatus}`, fetcher)
  const { data: invoicesSummary } = useSWR('/api/admin/invoices/summary', fetcher)
  const { data: insightsData, isLoading: insightsLoading, mutate: refreshInsights } = useSWR(`/api/admin/stripe/financial-insights?days=${days}`, fetcher, { revalidateOnFocus: false, dedupingInterval: 60000 })
  const { data: forecastData } = useSWR('/api/admin/stripe/forecast', fetcher, { revalidateOnFocus: false, dedupingInterval: 120000 })
  const { data: syncStatus, mutate: refreshSyncStatus } = useSWR('/api/admin/stripe/sync-status', fetcher, { revalidateOnFocus: false, dedupingInterval: 30000 })
  
  const refreshAll = () => {
    refreshRevenue(); refreshStripe(); refreshPromos(); refreshPayments()
    refreshSubs(); refreshInvoices(); refreshInsights(); refreshSyncStatus()
  }
  
  const syncStripeData = async () => {
    setSyncing(true)
    try {
      const result = await apiRequest('/api/admin/stripe/sync', { method: 'POST' })
      alert(`Synced: ${result.results.customers} customers, ${result.results.charges} charges, ${result.results.subscriptions} subscriptions`)
      refreshAll()
    } catch (err) { alert('Sync error: ' + err.message) }
    finally { setSyncing(false) }
  }

  const createPromoCode = async (e) => {
    e.preventDefault()
    if (!newPromo.code) return
    setCreating(true)
    try {
      await apiRequest('/api/admin/promo-codes', { method: 'POST', body: JSON.stringify(newPromo) })
      refreshPromos()
      setNewPromo({ code: '', plan: 'premium', duration_days: 365, max_uses: 1, description: '' })
    } catch (err) { alert(err.message) }
    finally { setCreating(false) }
  }
  
  const deletePromoCode = async (promoId) => {
    if (!confirm('Delete this promo code?')) return
    try { await apiRequest(`/api/admin/promo-codes/${promoId}`, { method: 'DELETE' }); refreshPromos() }
    catch (err) { alert(err.message) }
  }
  
  const handleUpgrade = async (teamId) => {
    if (!confirm('Upgrade this team to Premium?')) return
    setSubActionLoading(teamId)
    try { await apiRequest(`/api/admin/subscriptions/${teamId}/upgrade`, { method: 'POST' }); refreshSubs() }
    catch (err) { alert(err.message) } finally { setSubActionLoading(null) }
  }

  const handleDowngrade = async (teamId) => {
    if (!confirm('Downgrade this team to Free?')) return
    setSubActionLoading(teamId)
    try { await apiRequest(`/api/admin/subscriptions/${teamId}/downgrade`, { method: 'POST' }); refreshSubs() }
    catch (err) { alert(err.message) } finally { setSubActionLoading(null) }
  }

  const handleExtendTrial = async () => {
    if (!selectedTeam) return
    setSubActionLoading(selectedTeam.team_id)
    try {
      await apiRequest(`/api/admin/subscriptions/${selectedTeam.team_id}/extend-trial`, { method: 'POST', body: JSON.stringify({ days: extendDays }) })
      refreshSubs(); setShowExtendModal(false); setSelectedTeam(null)
    } catch (err) { alert(err.message) } finally { setSubActionLoading(null) }
  }
  
  const handleRefund = async (invoiceId) => {
    if (!confirm('Refund this invoice?')) return
    setInvActionLoading(invoiceId)
    try { await apiRequest(`/api/admin/invoices/${invoiceId}/refund`, { method: 'POST' }); refreshInvoices() }
    catch (err) { alert(err.message) } finally { setInvActionLoading(null) }
  }

  const handleCancelInvoice = async (invoiceId) => {
    if (!confirm('Cancel this invoice?')) return
    setInvActionLoading(invoiceId)
    try { await apiRequest(`/api/admin/invoices/${invoiceId}/cancel`, { method: 'POST' }); refreshInvoices() }
    catch (err) { alert(err.message) } finally { setInvActionLoading(null) }
  }
  
  const chartData = revenueData?.revenue_by_day?.map(d => ({
    date: d.date?.slice(5) || '',
    amount: (d.amount || 0) / 100
  })) || []

  return (
    <div className="space-y-6">
      <PageHeader title="Payments" description="Revenue, transactions, and promotions">
        <Button variant="outline" onClick={syncStripeData} disabled={syncing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Stripe'}
        </Button>
        <Button variant="ghost" size="icon" onClick={refreshAll} title="Refresh data">
          <RefreshCw className="w-5 h-5 text-gray-600" />
        </Button>
        <Select value={String(days)} onValueChange={(val) => setDays(Number(val))}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview"><DollarSign className="w-4 h-4 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="insights"><TrendingUp className="w-4 h-4 mr-1" />Insights</TabsTrigger>
          <TabsTrigger value="subscriptions"><Crown className="w-4 h-4 mr-1" />Subscriptions</TabsTrigger>
          <TabsTrigger value="invoices"><Receipt className="w-4 h-4 mr-1" />Invoices</TabsTrigger>
          <TabsTrigger value="transactions"><CreditCard className="w-4 h-4 mr-1" />Transactions</TabsTrigger>
          <TabsTrigger value="stripe"><ExternalLink className="w-4 h-4 mr-1" />Stripe</TabsTrigger>
          <TabsTrigger value="promos"><Tag className="w-4 h-4 mr-1" />Promo Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab revenueData={revenueData} revenueLoading={revenueLoading} stripeData={stripeData} chartData={chartData} syncStatus={syncStatus} />
        </TabsContent>
        <TabsContent value="insights">
          <InsightsTab insightsData={insightsData} insightsLoading={insightsLoading} refreshInsights={refreshInsights} forecastData={forecastData} syncing={syncing} syncStripeData={syncStripeData} />
        </TabsContent>
        <TabsContent value="subscriptions">
          <SubscriptionsTab subsData={subsData} subsLoading={subsLoading} subsSummary={subsSummary} subPage={subPage} setSubPage={setSubPage} subPlan={subPlan} setSubPlan={setSubPlan} subActionLoading={subActionLoading} handleUpgrade={handleUpgrade} handleDowngrade={handleDowngrade} showExtendModal={showExtendModal} setShowExtendModal={setShowExtendModal} selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam} extendDays={extendDays} setExtendDays={setExtendDays} handleExtendTrial={handleExtendTrial} />
        </TabsContent>
        <TabsContent value="invoices">
          <InvoicesTab invoicesData={invoicesData} invoicesLoading={invoicesLoading} invoicesSummary={invoicesSummary} invPage={invPage} setInvPage={setInvPage} invStatus={invStatus} setInvStatus={setInvStatus} invActionLoading={invActionLoading} handleRefund={handleRefund} handleCancelInvoice={handleCancelInvoice} />
        </TabsContent>
        <TabsContent value="transactions">
          <TransactionsTab paymentsData={paymentsData} paymentsLoading={paymentsLoading} paymentStatus={paymentStatus} setPaymentStatus={setPaymentStatus} paymentPage={paymentPage} setPaymentPage={setPaymentPage} />
        </TabsContent>
        <TabsContent value="stripe">
          <StripeTab stripeData={stripeData} stripeLoading={stripeLoading} webhookEvents={webhookEvents} refreshWebhooks={refreshWebhooks} />
        </TabsContent>
        <TabsContent value="promos">
          <PromosTab promoCodes={promoCodes} newPromo={newPromo} setNewPromo={setNewPromo} creating={creating} createPromoCode={createPromoCode} deletePromoCode={deletePromoCode} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
